import React, { useState } from "react";
import axios from "axios";
import { apiurl } from "../config/config";

export default function LogisticsSpecialist() {
  const [step, setStep] = useState(0);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState("");
  const [formData, setFormData] = useState({
    resume: "",
    name: "",
    phone: "",
    email: "",
    linkedin: "",
    logisticsExperience: "",
    shipmentAllocation: "",
    ndrHandling: "",
    relocate: "",
    salary: "",
    notice: "",
  });

  // ✅ Resume Upload
  const handleFileUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadMessage("");

    const formDataUpload = new FormData();
    formDataUpload.append("file", file);

    try {
      const response = await axios.post(
        `${apiurl}/api/media/resume`,
        formDataUpload
      );

      if (response?.data?.success) {
        setFormData((prev) => ({
          ...prev,
          [field]: response.data.fileUrl,
        }));
        setUploadMessage("✅ File uploaded successfully!");
      } else {
        setUploadMessage("❌ File upload failed. Please try again.");
      }
    } catch (error) {
      console.error("Upload error:", error);
      setUploadMessage("⚠️ Error uploading file.");
    } finally {
      setUploading(false);
    }
  };

  // Updated Steps for Logistics Specialist
  const steps = [
    { label: "Enter your Name", type: "text", key: "name", required: true },
    { label: "Enter your Phone Number", type: "text", key: "phone", required: true },
    { label: "Enter your Email", type: "email", key: "email", required: true },
    { label: "Enter your LinkedIn Profile URL", type: "url", key: "linkedin" },
    { label: "Name the last-mile logistics company you worked with and mention your total years of experience with them.", type: "textarea", key: "logisticsExperience", required: true },
    { label: "If you have multiple courier partners available for delivery, how do you decide shipment allocation?", type: "textarea", key: "shipmentAllocation", required: true },
    { label: "How do you handle Non-Delivery Report (NDR) shipments on a daily basis? What steps would you take to improve first attempt delivery success?", type: "textarea", key: "ndrHandling", required: true },
    { label: "Are you currently based in or willing to relocate to Noida/NCR region?", type: "radio", key: "relocate", options: ["Yes", "No"], required: true },
    { label: "What is your salary expectation? Why is it justified?", type: "textarea", key: "salary", required: true },
    { label: "If selected, are you an immediate joiner? If not, how soon can you join?", type: "text", key: "notice", required: true },
  ];

  const validateStep = () => {
    const { key, required, type } = steps[step - 2];
    const value = formData[key];

    if (!required) return true;
    if ((type === "text" || type === "textarea") && !value.trim()) {
      setError("This field is required.");
      return false;
    }
    if (type === "radio" && !value) {
      setError("Please select an option.");
      return false;
    }
    if (key === "phone") {
      const phoneRegex = /^[0-9]{10}$/;
      if (!phoneRegex.test(value)) {
        setError("Please enter a valid 10-digit phone number.");
        return false;
      }
    }
    if (key === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        setError("Please enter a valid email address.");
        return false;
      }
    }

    setError("");
    return true;
  };

  const handleChange = (e, key, type) => {
    const value = type === "checkbox" ? e.target.checked : e.target.value;
    setFormData({ ...formData, [key]: value });
  };

  const handleNext = () => {
    if (validateStep()) setStep((prev) => prev + 1);
  };
  const handleBack = () => setStep((prev) => prev - 1);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (step <= steps.length) {
        if (validateStep()) handleNext();
      } else {
        if (validateStep()) handleSubmit(e);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Final Payload:", formData);

    const answers = Object.keys(formData).map((key) => {
      const stepInfo = steps.find((s) => s.key === key);
      return { questionText: stepInfo ? stepInfo.label : key, answer: formData[key] };
    });

    try {
      await axios.post(`${apiurl}/api/online-application/create`, {
        jobId: "10000002",
        candidateId: "unique_candidate_id",
        answers,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        resume: formData.resume,
      });
      window.location.href = "/careers/thank-you";
    } catch (err) {
      console.error("Submission error:", err);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-white px-4">
      {step === 0 ? (
        // Intro Screen for Logistics Specialist
        <div className="text-center bg-white p-10 rounded-2xl shadow-xl max-w-3xl w-full">
          <h1 className="text-[16px] md:text-[24px] font-extrabold text-green-700 mb-6 leading-tight">
            🚛 Scope of Work & Growth Opportunity as Logistics Specialist 🚛
          </h1>
          <p className="text-gray-600 text-[12px] md:text-[16px] leading-relaxed text-left mb-6">
            Join SUWASTHI as a Logistics Specialist and handle last-mile delivery operations, courier partner management, and ensure seamless order fulfillment with high first-attempt delivery success.
          </p>
          {/* <ul className="text-left text-gray-700 text-[12px] md:text-[16px] list-disc list-inside space-y-3 mb-8">
            <li>Manage multiple courier partners and optimize shipment allocation.</li>
            <li>Track and resolve NDR (Non-Delivery Reports) efficiently.</li>
            <li>Ensure smooth last-mile delivery and improve operational KPIs.</li>
            <li>Work closely with supply chain & operations teams for better customer experience.</li>
          </ul> */}
          <button
            onClick={() => setStep(1)}
            className="mt-4 px-8 py-4 bg-green-600 text-white text-lg font-semibold rounded-xl shadow-lg hover:bg-green-700"
          >
            Let’s Start 🚀
          </button>
        </div>
      ) : step === 1 ? (
        // Resume Upload Screen
        <div className="text-center bg-white p-8 rounded-2xl shadow-xl max-w-lg w-full">
          <h1 className="text-3xl font-bold text-green-700 mb-4">📄 Upload Your Resume</h1>
          {!formData.resume ? (
            <label
              htmlFor="dropzone-file"
              className="flex flex-col items-center justify-center w-full h-64 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition"
            >
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <svg className="w-8 h-8 mb-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 
                    5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5
                    a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                  />
                </svg>
                <p className="mb-2 text-sm text-gray-500">
                  <span className="font-semibold">Click to upload</span> or drag & drop
                </p>
                <p className="text-xs text-gray-400">PDF, DOC, DOCX (Max 5MB)</p>
              </div>
              <input id="dropzone-file" accept=".pdf,.doc,.docx" type="file" className="hidden" onChange={(e) => handleFileUpload(e, "resume")} />
            </label>
          ) : (
            <div>
              <p className="text-green-700">✅ File uploaded</p>
              <button onClick={() => setFormData((prev) => ({ ...prev, resume: "" }))}>Remove</button>
            </div>
          )}
          <button onClick={() => setStep(2)} disabled={!formData.resume} className="mt-6 px-6 py-3 bg-green-600 text-white rounded-xl disabled:opacity-50">
            Continue ➡️
          </button>
        </div>
      ) : (
        // Question Form
        <form onSubmit={handleSubmit} onKeyDown={handleKeyDown} className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-lg">
          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>Question {step - 1} of {steps.length}</span>
              <span>{Math.round(((step - 1) / steps.length) * 100)}% Complete</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-green-600 h-2 rounded-full transition-all" style={{ width: `${((step - 1) / steps.length) * 100}%` }} />
            </div>
          </div>

          <label className="block mb-2 text-gray-700 font-medium">{steps[step - 2].label}</label>

          {steps[step - 2].type === "textarea" ? (
            <textarea rows="4" value={formData[steps[step - 2].key]} onChange={(e) => handleChange(e, steps[step - 2].key, "text")} className="w-full border rounded-lg p-3" />
          ) : steps[step - 2].type === "radio" ? (
            <div className="flex gap-6">
              {steps[step - 2].options.map((opt) => (
                <label key={opt} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name={steps[step - 2].key}
                    value={opt}
                    checked={formData[steps[step - 2].key] === opt}
                    onChange={(e) => handleChange(e, steps[step - 2].key, "radio")}
                  />
                  {opt}
                </label>
              ))}
            </div>
          ) : (
            <input type={steps[step - 2].type} value={formData[steps[step - 2].key]} onChange={(e) => handleChange(e, steps[step - 2].key, steps[step - 2].type)} className="w-full border rounded-lg p-3" />
          )}

          {error && <p className="text-red-500 text-sm mt-2">{error}</p>}

          <div className="flex justify-between mt-6">
            {step > 2 && <button type="button" onClick={handleBack} className="px-4 py-2 bg-gray-200 rounded-lg">Back</button>}
            {step - 1 < steps.length ? (
              <button type="button" onClick={handleNext} className="px-4 py-2 bg-green-600 text-white rounded-lg">Next</button>
            ) : (
              <button type="submit" onClick={(e) => { e.preventDefault(); if (validateStep()) handleSubmit(e); }} disabled={!formData[steps[step - 2].key]?.trim()} className="px-4 py-2 bg-green-600 text-white rounded-lg disabled:opacity-50">Submit</button>
            )}
          </div>
        </form>
      )}
    </div>
  );
}
